import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { Divider, TextField, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateAppointment } from '../../../hooks/appointments/useCreateAppointment';
import { useUpdateAppointment } from '../../../hooks/appointments/useUpdateAppointment';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import AppointmentStatusSelect from '../controls/AppointmentStatusSelect';
import DateTimePicker from '../../common/controls/DateTimePicker';
import { formatUtcToLocal } from '../../../utils/formatUtcToLocal';
import { isEqualToOrOlderThanToday } from '../../../utils/isEqualToOrOlderThanToday';
import { isPastDate } from '../../../utils/isPastDate';
import CallSmsButtonCombo from '../../common/controls/CallSMSButtonCombo';
import CopyButton from '../../common/controls/CopyButton';
import DateAppointmentsView from '../modals/DateAppointmentsView';
import dayjs from 'dayjs';

export default function AppointmentForm({ 
  successCallback = null, 
  history = null, 
  historyEntry = null, 
  pet = null, 
  appointment = null, 
  recommendedVisit = null,
  mode = "create", 
  open, onClose 
}) {

    const { t } = useTranslation('common'); 

    const defaultDate = new Date(); 
    defaultDate.setDate(defaultDate.getDate() + 14); 
    
    const [dateAppointmentViewOpen, setDateAppointmentViewOpen] = React.useState(false);

    // Round up to the next hour 
    defaultDate.setHours(defaultDate.getHours() + 1, 0, 0, 0);

    const [dateTime, setDateTime] = React.useState("");
    const [remarks, setRemarks] = React.useState("" || (recommendedVisit?.recommended_visit || ""));
    const [statusId, setStatusId] = React.useState("");
    const [ogStatusId, setOgStatusId] = React.useState("");

    const { create, creating } = useCreateAppointment(successCallback);
    const { update, updating } = useUpdateAppointment(successCallback);

    const { showSnackbar } = useSnackbar();

    const [disabledStatuses, setDisabledStatuses] = React.useState([]);

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        if (open)
        {
          setDateTime(recommendedVisit?.visit_date || appointment?.datetime || defaultDate || "");
          setRemarks(appointment?.remarks || "" || (recommendedVisit?.recommended_visit || ""));
          setStatusId(appointment?.status_id || "");
          setOgStatusId(appointment?.status_id || "");
          if (appointment?.status_id === 1) {
            if (!isEqualToOrOlderThanToday(appointment.datetime)) {
              setDisabledStatuses([5, 6]);
            }
          }
          if (appointment?.status_id === 2) {
            if (isEqualToOrOlderThanToday(appointment.datetime)) {
              setDisabledStatuses([1]);
            }
            else {
              setDisabledStatuses([1,5,6]);
            }
            
          }
        }
    }, [appointment, open]);

    const handleDateTimeChange = (dateTime) => {
        if (dateTime && dateTime.formatted) {
            setDateTime(dateTime.formatted);
        }
    };

    const handleStatusSelect = (selectedStatus) => {
      setStatusId(selectedStatus);
    }

    const handleSave = async () => {
        if (!dateTime.trim()) {
            showSnackbar(t('appointments.messages.datetime_required'), "warning");
            return;
        }
        if ((mode === "create") && isPastDate(dateTime)) {
            showSnackbar(t('appointments.messages.datetime_must_be_future'), "warning");
            return;
        }
        try 
        {
          if (mode === "create") {
            try {
              await create({ 
                pet_id: pet.id,
                pet_owner_id: pet.owner.id,
                history_id: history ? history.id : null,
                history_entry_id: historyEntry ? historyEntry.id : null,
                datetime: dateTime,
                remarks: remarks,
                status_id: 1,
                date_string: formatUtcToLocal(dateTime)
              });
              showSnackbar(t('appointments.messages.create_success'), "success");
              // clear field after save
              setDateTime(defaultDate);
              setRemarks("");
              setStatusId("");
              onClose();     // close dialog
            } catch(error)
            {
              if (error.status === 409)
              {
                showSnackbar(t("appointments.messages.conflict"), "error");
              }
              else {
                showSnackbar(t('appointments.messages.save_fail'), "error");
              }
            }  
          } else if (mode === "edit" && appointment) {
              try {
                await update(appointment.id, { 
                  datetime: dateTime,
                  remarks: remarks,
                  status_id: statusId,
                  date_string: formatUtcToLocal(dateTime)
                });
                showSnackbar(t('appointments.messages.update_success'), "success");
                // clear field after save
                setDateTime(defaultDate);
                setRemarks("");
                setStatusId("");
                onClose();     // close dialog
              } catch(error)
              {
                if (error.status === 409)
                {
                  showSnackbar(t("appointments.messages.conflict"), "error");
                }
                else {
                  showSnackbar(t('appointments.messages.save_fail'), "error");
                }
              }
          }
        } catch (err) {
          showSnackbar(t('appointments.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
      setDateTime(defaultDate);
      setRemarks("");
      setStatusId("");
      onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Create Appointment" : "Edit Appointment"}</DialogTitle>

        <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Patient</Typography>
            <Typography variant="body1" sx={{ mt: 0 }}>{`${pet?.name || appointment?.pet.name} (${pet?.owner?.first_name || appointment?.petOwner.first_name} ${pet?.owner?.last_name || appointment?.petOwner.last_name})`}</Typography>
            <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Contact Number</Typography> 
            <Box display="inline-flex" alignItems="center">
              <Typography variant="body1" sx={{ mt: 0 }}>{`${pet?.owner?.contact_number || appointment?.petOwner.contact_number}`}</Typography>
              <CopyButton text={pet?.owner?.contact_number || appointment?.petOwner.contact_number} />
            </Box>
            <CallSmsButtonCombo phoneNumber={pet?.owner?.contact_number || appointment?.petOwner.contact_number} bodyText={`Hello ${pet?.owner?.first_name || appointment?.petOwner.first_name},`} />
            <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>E-mail</Typography>
            <Box display="inline-flex" alignItems="center">
              <Typography variant="body1" sx={{ mt: 0 }}>{`${pet?.owner?.email || appointment?.petOwner.email}`}</Typography>
              <CopyButton text={pet?.owner?.email || appointment?.petOwner.email} />
            </Box>

            <Divider sx={{ my: 2 }}/>
            <Stack spacing={1}>
              {mode === 'edit' ?
              <>
              <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Date and Time</Typography>
              <Typography variant="body1" sx={{ mt: 0 }}>{formatUtcToLocal(appointment.datetime)}</Typography>
              </>
              :
              <>
              <DateTimePicker 
                defaultValue={dateTime || ""} 
                size='small'
                onChange={handleDateTimeChange} 
                label="Date and Time"
                restrict="future" 
                minYear={new Date().getFullYear()}
                maxYear={new Date().getFullYear() + 1}
                monthFormat="short"
                order={["year", "month", "day"]}
                gap={1}
                controlWidths={{ month: 2, day: 2, year: 8 }}
                placeholders={{ month: "Month", day: "Day", year: "Year" }}
                minuteStep={5}
              />
              <Button 
                variant="outlined" 
                size="small" 
                fullWidth
                onClick={() => setDateAppointmentViewOpen(true)}
              >
                Check Same Day Appointments
              </Button>
              </>
              }
              {appointment?.status_id ?
              <AppointmentStatusSelect 
                statusId={statusId} 
                onSelect={handleStatusSelect}
                disabledOptions={disabledStatuses}
              />
              : null }
              
              <TextField
                  disabled={mode === 'edit'}
                  label="Remarks to Patient"
                  variant="outlined"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
              />
              {statusId === "" ?
              <Typography variant="body2" color='warning' sx={{ mb: 0 }}>{t('appointments.messages.will_send_email', { name: `${pet?.owner?.first_name || appointment?.petOwner.first_name} ${pet?.owner?.last_name || appointment?.petOwner.last_name}`, email: pet?.owner?.email || appointment?.petOwner.email })}</Typography>
              : null}
              {!isPastDate(appointment?.datetime) && (statusId === 3 || statusId === 4) ?
              <Typography variant="body2" color='warning' sx={{ mb: 0 }}>{t('appointments.messages.will_send_cancel_email', { name: `${pet?.owner?.first_name || appointment?.petOwner.first_name} ${pet?.owner?.last_name || appointment?.petOwner.last_name}`, email: pet?.owner?.email || appointment?.petOwner.email })}</Typography>
              : null}
               {isPastDate(appointment?.datetime) && (statusId === 3 || statusId === 4) ?
              <Typography variant="body2" color='warning' sx={{ mb: 0 }}>{t('appointments.messages.will_lock', { name: `${pet?.owner?.first_name || appointment?.petOwner.first_name} ${pet?.owner?.last_name || appointment?.petOwner.last_name}`, email: pet?.owner?.email || appointment?.petOwner.email })}</Typography>
              : null}
              {statusId === 5 || statusId === 6 ?
              <Typography variant="body2" color='warning' sx={{ mb: 0 }}>{t('appointments.messages.will_lock', { name: `${pet?.owner?.first_name || appointment?.petOwner.first_name} ${pet?.owner?.last_name || appointment?.petOwner.last_name}`, email: pet?.owner?.email || appointment?.petOwner.email })}</Typography>
              : null}
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button disabled={(creating || updating) || (mode === 'edit' && (statusId === ogStatusId))} onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        {dateTime ?
        <DateAppointmentsView
            open={dateAppointmentViewOpen}
            onClose={() => setDateAppointmentViewOpen(false)}
            date={dayjs(dateTime).format("YYYY-MM-DD")}
        />
        : null }
        </>
    );
}